'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _SelectPrimitive$Item;
var _excluded = ["className", "children", "unstyled", "renderIcon"],
  _excluded2 = ["className", "children", "position", "removePortal"],
  _excluded3 = ["className"],
  _excluded4 = ["className", "children", "removeCheck"],
  _excluded5 = ["className"];
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import ArrowIosDown from '@crehana/compass.icons.outline/ArrowIosDown';
import ArrowIosUp from '@crehana/compass.icons.outline/ArrowIosUp';
import Checkmark from '@crehana/compass.icons.outline/Checkmark';
import { cn } from "../utils";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    children = _ref.children,
    unstyled = _ref.unstyled,
    renderIconProp = _ref.renderIcon,
    props = _objectWithoutProperties(_ref, _excluded);
  var renderIcon = function renderIcon() {
    if (renderIconProp) {
      return renderIconProp();
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ArrowIosDown, {
        size: 24,
        className: cn('arrow-ios-down group-data-[state=open]:tw-hidden', props.disabled ? 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600' : 'tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500')
      }), /*#__PURE__*/_jsx(ArrowIosUp, {
        size: 24,
        className: cn('arrow-ios-up group-data-[state=closed]:tw-hidden', props.disabled ? 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600' : 'tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500')
      })]
    });
  };
  var icon = renderIcon();
  return unstyled ? /*#__PURE__*/_jsx(SelectPrimitive.Trigger, _objectSpread(_objectSpread({
    ref: ref,
    className: cn('tw-group tw-cursor-pointer tw-justify-between tw-flex tw-items-center', className)
  }, props), {}, {
    children: /*#__PURE__*/_jsx("span", {
      className: "tw-line-clamp-1 tw-grow tw-text-left",
      children: children
    })
  })) : /*#__PURE__*/_jsxs(SelectPrimitive.Trigger, _objectSpread(_objectSpread({
    ref: ref,
    className: cn('tw-group tw-cursor-pointer tw-justify-between tw-input tw-flex tw-items-center', className)
  }, props), {}, {
    children: [/*#__PURE__*/_jsx("span", {
      className: "tw-line-clamp-1 tw-grow tw-text-left",
      children: children
    }), icon ? /*#__PURE__*/_jsx(SelectPrimitive.Icon, {
      children: /*#__PURE__*/_jsx("div", {
        className: "tw-flex tw-items-center tw-shrink-0",
        children: icon
      })
    }) : null]
  }));
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectContent = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    children = _ref2.children,
    _ref2$position = _ref2.position,
    position = _ref2$position === void 0 ? 'popper' : _ref2$position,
    removePortal = _ref2.removePortal,
    props = _objectWithoutProperties(_ref2, _excluded2);
  var Wrapper = removePortal ? React.Fragment : SelectPrimitive.Portal;
  return /*#__PURE__*/_jsx(Wrapper, {
    children: /*#__PURE__*/_jsx(SelectPrimitive.Content, _objectSpread(_objectSpread({
      ref: ref,
      className: cn('tw-max-h-24 tw-z-popover tw-overflow-auto tw-w-full tw-rounded', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700 dark:tw-shadow-none', 'tw-relative tw-z-50 rounded-md border border-neutral-200 bg-white text-neutral-950 tw-shadow-03', 'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95', 'data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2', position === 'popper' && 'data-[side=bottom]:tw-translate-y-1 data-[side=left]:-tw-translate-x-1 data-[side=right]:tw-translate-x-1 data-[side=top]:-tw-translate-y-1', className),
      position: position
    }, props), {}, {
      children: /*#__PURE__*/_jsx(SelectPrimitive.Viewport, {
        className: cn('', position === 'popper' && 'h-[var(--radix-select-trigger-height)] tw-w-full tw-min-w-[var(--radix-select-trigger-width)]'),
        style: {
          overflow: undefined
        },
        children: children
      })
    }))
  });
});
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var className = _ref3.className,
    props = _objectWithoutProperties(_ref3, _excluded3);
  return /*#__PURE__*/_jsx(SelectPrimitive.Label, _objectSpread({
    ref: ref,
    className: cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)
  }, props));
});
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var className = _ref4.className,
    children = _ref4.children,
    removeCheck = _ref4.removeCheck,
    props = _objectWithoutProperties(_ref4, _excluded4);
  return /*#__PURE__*/_jsxs(SelectPrimitive.Item, _objectSpread(_objectSpread({
    ref: ref,
    className: cn('tw-group tw-relative tw-flex tw-gap-1.2 tw-w-full tw-justify-between tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-outline-none', 'tw-font-body4 tw-py-1.2 tw-px-2.4 tw-h-4.8', 'data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50', 'hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600 hover:tw-text-primary-light-500', 'data-[state=checked]:tw-text-primary-light-500 dark:data-[state=checked]:tw-bg-neutral-dark-600 data-[state=checked]:tw-bg-neutral-light-300 dark:data-[state=checked]:bg-neutral-dark-600', 'dark:tw-text-neutral-light-100', className)
  }, props), {}, {
    children: [/*#__PURE__*/_jsx(SelectPrimitive.ItemText, {
      children: children
    }), !removeCheck && (_SelectPrimitive$Item || (_SelectPrimitive$Item = /*#__PURE__*/_jsx(SelectPrimitive.ItemIndicator, {
      children: /*#__PURE__*/_jsx(Checkmark, {
        size: 24,
        className: "tw-text-success-300"
      })
    })))]
  }));
});
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
    props = _objectWithoutProperties(_ref5, _excluded5);
  return /*#__PURE__*/_jsx(SelectPrimitive.Separator, _objectSpread({
    ref: ref,
    className: cn('-mx-1 my-1 h-px bg-neutral-100 dark:bg-neutral-800', className)
  }, props));
});
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator };